import { injectable } from 'inversify';
import { UniversalSurfaceRenderer } from '../../../../platform/UniversalSurfaceRenderer';
import { Matrix } from '../../../../platform/geom/Matrix';
import { Point } from '../../../../platform/geom/Point';
import { Colors } from '../../../../ui';
import { resolve } from '../../../Container';
import { ConnectTarget } from '../../../packages/connector/ConnectTarget';
import { ConnectInfo, Connectable } from '../../../packages/connector/Connectable';
import { Camera } from '../../Camera';
import { EditorSession } from '../../EditorSession';
import { GridSystem } from '../../GridSystem';
import { Selection } from '../../Selection';
import { bringToFront } from '../../commands/OrderCommands';
import { AutoBringToFront } from '../../entities/AutoBringToFront';
import { Phys } from '../../entities/Phys';
import { Figure, Physical, RelativeTo, TransformScope } from '../../entities/Physical';
import { SpatialIndex } from '../../utilities/SpatialIndex';
import { ToolOverlay } from '../ToolOverlay';
import { SelectionTransformation } from './SelectionTransformation';

export interface SelectionTweakTransformationConfig {
    offset: number;
}

@injectable()
export class SelectionTweakTransformation extends SelectionTransformation {

    public readonly type = 'tweak';

    @resolve(Camera) private camera: Camera;
    @resolve(GridSystem) private grid: GridSystem;
    @resolve(EditorSession) private session: EditorSession;
    @resolve(ToolOverlay) private overlay: ToolOverlay;
    @resolve(SpatialIndex) private spatial: SpatialIndex;

    // private baseValues = new Map<Physical, Figure>();
    // private swapValues = new Map<Physical, Figure>();
    // private selection: Physical[] = [];
    private baseValue = null as Figure;

    private subject = null as Physical;
    private offset = null as number;
    private handle = null as Point;
    private snapping: boolean = false;
    private potentialConnect = null as ConnectInfo;
    // private bounds: Rect = null;
    // private fixedPlane: Line = null;

    public configure(config: SelectionTweakTransformationConfig) {
        this.offset = config.offset;
        this.burden(
            this.overlay.add(this.paint.bind(this))
        );
    }

    protected initialize(): void {
        this.subject = this.session.get(Selection).first();
        this.baseValue = this.subject.createFigure();
        this.handle = this.baseValue[this.offset];
        this.snapping = this.grid.shouldSnap(this.subject);
        // this.baseValues = Figure.fromManyAsMap(this.selection);
        // this.bounds = Physicals.bounds(this.selection);
        // this.fixedPlane = computeSingleArbitrarySwapPlane(this.selection);
        // this.burden(this.overlay.add(this.paintState));

        if (this.subject.is(AutoBringToFront)) {
            this.update(() => this.dispatch(bringToFront, [this.subject.id]));
        }
    }

    protected apply(): void {

        const initial = this.handle;
        const latest = this.snapping
            ? this.grid.snap(this.latest.position)
            : this.latest.position;

        const transform = Matrix.identity.translate(latest.subtract(initial));

        this.update(() => {
            this.applyTransform(transform);
            this.updateConnect(latest);
        });
    }

    protected commit(): void {
        this.update(u => {
            // Apply destination
            // this.dispatch(place, [ids, false]);
            // this.dispatch(swallow, [ids]);
            // Use noop just to be sure changes were made
            this.applyConnect();
            this.emit('commit', [[this.subject.id]]);
            u.noop();
        });
    }

    private applyTransform(transform: Matrix) {
        if (!this.subject) return;
        const scope = new TransformScope([this.subject], 'tweak');
        const from = this.baseValue;
        const to = from.map((x, i) => i === this.offset ? transform.apply(x) : x);
        this.subject.applyTransformation(scope, from, to);
    }

    private updateConnect(latest: Point) {

        const connectable = this.subject.as(Connectable);
        if (!connectable) return;

        const target = Phys
            .from(this.spatial.lookup(latest, ConnectTarget))
            .filter(x => !x.bounded())
            .highest()
            ?.as(ConnectTarget);

        this.potentialConnect = !!target
            ? connectable.queryConnect(this.offset, latest, target)
            : null;

        this.overlay.repaint();
    }

    private applyConnect() {
        const { command } = this.potentialConnect ?? {};
        if (!command) return;
        this.dispatch(command);
    }

    private paint(gfx: UniversalSurfaceRenderer) {

        const { target } = this.potentialConnect ?? {};
        if (!target) return;

        gfx.setTransform(this.camera.modelViewMatrix);
        gfx.strokeStyle = Colors.primaryLight;
        gfx.lineCap = 'round';

        gfx.lineWidth = 3;
        gfx.stroke(target.path(RelativeTo.Model).toPath2D());
    }
}
